import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import { FALLBACK_FISHING_HONORARIO_CENTS } from '@/pages/gestiones/honorario';
import type {
  LicenseCheckoutRequirement,
  LicenseDurationOption,
  LicenseFeeVariant,
  LicenseFeeVariantKind,
  LicenseOpsFilter,
  LicenseOrderDetail,
  LicenseOrderListItem,
  LicenseOrderStatusFilter,
  LicenseOrdersStats,
  LicenseRecipe,
  LicenseRecipeCreateInput,
  LicenseRecipeUpdateInput,
  LicenseSaleMode,
  OpsPriority,
  SimulateLicenseOrderResult,
  WaitingOn,
  WorkflowStage,
} from '../types';

const QK = {
  stats: ['admin', 'license-orders', 'stats'] as const,
  list: (status: LicenseOrderStatusFilter, opsFilter: LicenseOpsFilter, search: string) =>
    ['admin', 'license-orders', 'list', status, opsFilter, search] as const,
  detail: (id: string | null) => ['admin', 'license-orders', 'detail', id] as const,
  recipes: ['admin', 'license-recipes'] as const,
};

/** RPC rows vienen en snake_case */
interface RpcListRow {
  id: string;
  public_id: string;
  status: LicenseOrderListItem['status'];
  ccaa: string;
  modality: string;
  recipe_name: string;
  contact_email: string;
  contact_phone?: string | null;
  duration: string;
  exemption: string | null;
  service_fee_cents: number;
  official_fee_cents: number;
  user_id: string | null;
  paid_at: string | null;
  delivered_at: string | null;
  created_at: string;
  has_pdf: boolean;
  legal_invoice_id: string | null;
  legal_invoice_number: string | null;
  account_registered?: boolean | null;
  account_timing?: string | null;
  account_registered_at?: string | null;
  payment_source?: string | null;
  workflow_stage?: string | null;
  waiting_on?: string | null;
  next_action?: string | null;
  next_review_at?: string | null;
  priority?: string | null;
  assigned_to?: string | null;
  submitted_at?: string | null;
  last_milestone?: string | null;
  archived_at?: string | null;
}

interface RpcStatsRow {
  total_orders: number;
  awaiting_review: number;
  awaiting_3ds: number;
  in_progress: number;
  submitted?: number;
  delivered: number;
  refunded: number;
  failed: number;
  received: number;
  paid: number;
  abandoned?: number;
  account_before?: number | null;
  account_converted?: number | null;
  ops_waiting_us?: number | null;
  ops_waiting_alberto?: number | null;
  ops_waiting_client?: number | null;
  ops_waiting_administration?: number | null;
  ops_overdue?: number | null;
  ops_ready_to_submit?: number | null;
  ops_license_received?: number | null;
  ops_blocked?: number | null;
  ops_delivered?: number | null;
}

interface RpcRecipeRow {
  id: string;
  product: 'fishing_license';
  ccaa: string;
  modality: string;
  subterritorio?: string | null;
  tier: string;
  status: string;
  sale_mode?: string | null;
  whatsapp_message?: string | null;
  official_fee_cents: number;
  service_fee_cents: number | null;
  recipe_version: number;
  sede_url: string;
  display_name: string;
  last_synthetic_ok: boolean | null;
  has_paid_orders: boolean;
  checkout_requirements?: unknown;
  duration_options: Array<{
    code: string;
    label: string;
    label_en?: string | null;
    labelEn?: string | null;
    official_fee_cents?: number;
    officialFeeCents?: number;
    price_cents?: number;
    priceCents?: number;
    exemption?: string;
    duration_map?: string | null;
    durationMap?: string | null;
    fee_variants?: unknown;
    feeVariants?: unknown;
  }> | null;
}

function mapSaleMode(raw: unknown): LicenseSaleMode {
  return String(raw || '').trim().toLowerCase() === 'whatsapp' ? 'whatsapp' : 'online';
}

export const LICENSES_ATTENTION_WHATSAPP_PHONE_KEY = 'licenses.attention_whatsapp_phone';

const CHECKOUT_FLAGS = new Set<string>([
  'certificado_medico',
  'documentacion_embarcacion',
  'examen_aprobado',
  'empadronamiento',
  'dni_o_nie_obligatorio',
  'seguro_rc_accidentes',
]);

const FEE_VARIANT_KINDS = new Set<string>([
  'eslora',
  'residente',
  'recargo',
  'general',
  'other',
]);

function mapFeeVariants(raw: unknown): LicenseFeeVariant[] {
  if (!Array.isArray(raw)) return [];
  const variants: LicenseFeeVariant[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const code = typeof row.code === 'string' ? row.code.trim() : '';
    if (!code) continue;
    const label =
      typeof row.label === 'string' && row.label.trim() ? row.label.trim() : code;
    const officialFeeCents =
      typeof row.official_fee_cents === 'number'
        ? row.official_fee_cents
        : typeof row.officialFeeCents === 'number'
          ? row.officialFeeCents
          : 0;
    const kindRaw =
      typeof row.kind === 'string' ? row.kind.trim().toLowerCase() : '';
    const labelEnRaw =
      row.label_en !== undefined ? row.label_en : (row as { labelEn?: unknown }).labelEn;
    const labelEn =
      typeof labelEnRaw === 'string' ? labelEnRaw.trim().slice(0, 80) : '';
    const variant: LicenseFeeVariant = {
      code,
      label,
      officialFeeCents: Math.max(0, Math.round(officialFeeCents)),
      ...(labelEn ? { labelEn } : {}),
    };
    if (FEE_VARIANT_KINDS.has(kindRaw)) {
      variant.kind = kindRaw as LicenseFeeVariantKind;
    }
    if (row.additive === true) variant.additive = true;
    variants.push(variant);
  }
  return variants;
}

function mapCheckoutRequirements(raw: unknown): LicenseCheckoutRequirement[] {
  if (!Array.isArray(raw)) return [];
  const out: LicenseCheckoutRequirement[] = [];
  for (const item of raw) {
    const flag =
      typeof item === 'string'
        ? item.trim().toLowerCase()
        : item && typeof item === 'object'
          ? String(
              (item as Record<string, unknown>).flag ??
                (item as Record<string, unknown>).code ??
                ''
            )
              .trim()
              .toLowerCase()
          : '';
    if (!CHECKOUT_FLAGS.has(flag)) continue;
    if (!out.includes(flag as LicenseCheckoutRequirement)) {
      out.push(flag as LicenseCheckoutRequirement);
    }
  }
  return out;
}

function mapDurationOptions(raw: RpcRecipeRow['duration_options']): LicenseDurationOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((d) => d && typeof d.code === 'string')
    .map((d) => {
      const officialFeeCents =
        typeof d.official_fee_cents === 'number'
          ? d.official_fee_cents
          : typeof d.officialFeeCents === 'number'
            ? d.officialFeeCents
            : 0;
      const priceCents =
        typeof d.price_cents === 'number'
          ? d.price_cents
          : typeof d.priceCents === 'number'
            ? d.priceCents
            : officialFeeCents +
              Math.max(FALLBACK_FISHING_HONORARIO_CENTS, Math.round(officialFeeCents * 0.4));
      const rawMap = d.duration_map !== undefined ? d.duration_map : d.durationMap;
      const durationMap =
        rawMap === null || rawMap === ''
          ? null
          : typeof rawMap === 'string'
            ? rawMap
            : undefined;
      const feeVariants = mapFeeVariants(d.fee_variants ?? d.feeVariants);
      const labelEnRaw =
        d.label_en !== undefined ? d.label_en : (d as { labelEn?: unknown }).labelEn;
      const labelEn =
        typeof labelEnRaw === 'string' && labelEnRaw.trim()
          ? labelEnRaw.trim().slice(0, 80)
          : null;
      return {
        code: d.code,
        label: d.label || d.code,
        labelEn,
        priceCents,
        officialFeeCents,
        exemption: d.exemption,
        durationMap,
        ...(feeVariants.length ? { feeVariants } : {}),
      };
    });
}

function asWorkflowStage(raw: unknown): WorkflowStage | null {
  const v = String(raw || '');
  const allowed: WorkflowStage[] = [
    'new_order',
    'collecting_data',
    'ready_to_submit',
    'fee_pending',
    'fee_paid',
    'submitted',
    'awaiting_administration',
    'requirement_received',
    'license_received',
    'ready_to_deliver',
    'delivered',
    'blocked',
    'cancelled',
  ];
  return allowed.includes(v as WorkflowStage) ? (v as WorkflowStage) : null;
}

function asWaitingOn(raw: unknown): WaitingOn | null {
  const v = String(raw || '');
  return v === 'us' || v === 'alberto' || v === 'client' || v === 'administration' || v === 'none'
    ? v
    : null;
}

function asPriority(raw: unknown): OpsPriority | null {
  return raw === 'urgent' || raw === 'normal' ? raw : null;
}

function mapList(row: RpcListRow): LicenseOrderListItem {
  return {
    id: row.id,
    publicId: row.public_id,
    status: row.status,
    ccaa: row.ccaa,
    modality: row.modality,
    recipeName: row.recipe_name,
    contactEmail: row.contact_email,
    contactPhone: row.contact_phone ?? null,
    duration: row.duration,
    exemption: row.exemption,
    serviceFeeCents: row.service_fee_cents,
    officialFeeCents: row.official_fee_cents,
    userId: row.user_id,
    paidAt: row.paid_at,
    deliveredAt: row.delivered_at,
    createdAt: row.created_at,
    hasPdf: row.has_pdf,
    legalInvoiceId: row.legal_invoice_id ?? null,
    legalInvoiceNumber: row.legal_invoice_number ?? null,
    accountRegistered:
      row.account_registered === undefined || row.account_registered === null
        ? null
        : Boolean(row.account_registered),
    accountTiming:
      row.account_timing === 'before' ||
      row.account_timing === 'same_day' ||
      row.account_timing === 'after'
        ? row.account_timing
        : null,
    accountRegisteredAt: row.account_registered_at ?? null,
    paymentSource: row.payment_source ?? null,
    workflowStage: asWorkflowStage(row.workflow_stage),
    waitingOn: asWaitingOn(row.waiting_on),
    nextAction: row.next_action ?? null,
    nextReviewAt: row.next_review_at ?? null,
    priority: asPriority(row.priority),
    assignedTo: row.assigned_to ?? null,
    submittedAt: row.submitted_at ?? null,
    lastMilestone: row.last_milestone ?? null,
    archivedAt: row.archived_at ?? null,
  };
}

export function useLicenseOrdersStats() {
  return useQuery<LicenseOrdersStats | null>({
    queryKey: QK.stats,
    queryFn: async () => {
      const { data, error } = await dbClient.rpc<RpcStatsRow>('admin_license_orders_stats', {});
      if (error) throw new Error(error.message);
      const row = normalizeRpcResponse(data)[0] as RpcStatsRow | undefined;
      if (!row) return null;
      return {
        totalOrders: Number(row.total_orders || 0),
        awaitingReview: Number(row.awaiting_review || 0),
        awaiting3ds: Number(row.awaiting_3ds || 0),
        inProgress: Number(row.in_progress || 0),
        submitted: Number(row.submitted || 0),
        delivered: Number(row.delivered || 0),
        refunded: Number(row.refunded || 0),
        failed: Number(row.failed || 0),
        received: Number(row.received || 0),
        paid: Number(row.paid || 0),
        abandoned: Number(row.abandoned || 0),
        accountBefore:
          row.account_before === undefined || row.account_before === null
            ? null
            : Number(row.account_before),
        accountConverted:
          row.account_converted === undefined || row.account_converted === null
            ? null
            : Number(row.account_converted),
        opsWaitingUs: numOrNull(row.ops_waiting_us),
        opsWaitingAlberto: numOrNull(row.ops_waiting_alberto),
        opsWaitingClient: numOrNull(row.ops_waiting_client),
        opsWaitingAdministration: numOrNull(row.ops_waiting_administration),
        opsOverdue: numOrNull(row.ops_overdue),
        opsReadyToSubmit: numOrNull(row.ops_ready_to_submit),
        opsLicenseReceived: numOrNull(row.ops_license_received),
        opsBlocked: numOrNull(row.ops_blocked),
        opsDelivered: numOrNull(row.ops_delivered),
      };
    },
    staleTime: 30_000,
  });
}

function numOrNull(value: number | null | undefined): number | null {
  if (value === undefined || value === null) return null;
  return Number(value);
}

export function useLicenseOrdersList(
  status: LicenseOrderStatusFilter,
  search: string,
  opsFilter: LicenseOpsFilter = 'all'
) {
  return useQuery<LicenseOrderListItem[]>({
    queryKey: QK.list(status, opsFilter, search),
    queryFn: async () => {
      const params: Record<string, unknown> = { p_limit: 200 };
      if (status !== 'all') params.p_status = status;
      if (search.trim()) params.p_search = search.trim();
      if (opsFilter !== 'all') params.p_ops_filter = opsFilter;
      const { data, error } = await dbClient.rpc<RpcListRow>('admin_license_orders_list', params);
      if (error) throw new Error(error.message);
      return (normalizeRpcResponse(data) as RpcListRow[]).map(mapList);
    },
    staleTime: 30_000,
  });
}

export function useLicenseOrderDetail(orderId: string | null) {
  return useQuery<LicenseOrderDetail | null>({
    queryKey: QK.detail(orderId),
    enabled: Boolean(orderId),
    retry: 1,
    queryFn: async () => {
      if (!orderId) return null;
      const primary = await apiClient.get<LicenseOrderDetail>(
        `/ops/license-orders/${orderId}`
      );
      if (!primary.error) return primary.data ?? null;
      // La function dedicada puede no estar aún en el deploy vivo; fallback
      // sobre license-orders-create (siempre desplegada en producción).
      if (primary.error.status === 404) {
        const fallback = await apiClient.get<LicenseOrderDetail>(
          `/license-orders?orderId=${encodeURIComponent(orderId)}`
        );
        if (fallback.error) throw new Error(fallback.error.message);
        return fallback.data ?? null;
      }
      throw new Error(primary.error.message);
    },
  });
}

export function useLicenseRecipes() {
  return useQuery<LicenseRecipe[]>({
    queryKey: QK.recipes,
    queryFn: async () => {
      const { data, error } = await dbClient.rpc<RpcRecipeRow>(
        'admin_license_recipes_manage_list',
        {}
      );
      if (error) throw new Error(error.message);
      return (normalizeRpcResponse(data) as RpcRecipeRow[]).map((r) => ({
        id: r.id,
        product: r.product,
        ccaa: r.ccaa,
        modality: r.modality,
        subterritorio: typeof r.subterritorio === 'string' ? r.subterritorio : '',
        tier: r.tier,
        status: r.status,
        saleMode: mapSaleMode(r.sale_mode),
        whatsappMessage: typeof r.whatsapp_message === 'string' ? r.whatsapp_message : '',
        officialFeeCents: r.official_fee_cents,
        serviceFeeCents: r.service_fee_cents,
        recipeVersion: r.recipe_version,
        sedeUrl: r.sede_url,
        displayName: r.display_name,
        lastSyntheticOk: r.last_synthetic_ok,
        hasPaidOrders: Boolean(r.has_paid_orders),
        durationOptions: mapDurationOptions(r.duration_options),
        checkoutRequirements: mapCheckoutRequirements(r.checkout_requirements),
      }));
    },
  });
}

export function useLicensesAttentionWhatsAppPhone() {
  return useQuery<string>({
    queryKey: ['admin', 'licenses-attention-whatsapp-phone'],
    queryFn: async () => {
      const { data, error } = await dbClient.rpc<{ key: string; value: unknown }[]>(
        'admin_get_all_config',
        {}
      );
      if (error) throw new Error(error.message);
      const rows = normalizeRpcResponse(data) as { key: string; value: unknown }[];
      const row = rows.find((item) => item.key === LICENSES_ATTENTION_WHATSAPP_PHONE_KEY);
      if (!row) return '';
      if (typeof row.value === 'string') return row.value.trim();
      if (row.value == null) return '';
      return String(row.value).trim();
    },
  });
}

export function useUpdateLicensesAttentionWhatsAppPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (phoneDigits: string) => {
      const digits = phoneDigits.replace(/\D/g, '');
      const { error } = await dbClient.rpc('admin_upsert_app_config', {
        p_key: LICENSES_ATTENTION_WHATSAPP_PHONE_KEY,
        p_value: digits,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'licenses-attention-whatsapp-phone'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
    },
  });
}

export function useSimulateLicenseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.post<SimulateLicenseOrderResult>(
        '/ops/license-orders/simulate',
        body
      );
      if (error) throw new Error(error.message);
      if (!data?.id) throw new Error('Respuesta de simulación inválida');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'license-orders'] });
    },
  });
}

export function useCreateManualLicenseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      // orderId del path se ignora en create_manual_order (acción ops).
      const placeholder = '00000000-0000-4000-8000-000000000000';
      const payload = { ...body, action: 'create_manual_order' };
      const primary = await apiClient.post<{
        ok: boolean;
        id?: string;
        publicId?: string;
        duplicates?: unknown[];
      }>(`/ops/license-orders/${placeholder}/action`, payload);
      if (!primary.error) return primary.data;
      if (primary.error.status === 404) {
        const fallback = await apiClient.post<{
          ok: boolean;
          id?: string;
          publicId?: string;
        }>(`/license-orders?orderId=${placeholder}`, payload);
        if (fallback.error) throw new Error(fallback.error.message);
        return fallback.data;
      }
      throw new Error(primary.error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'license-orders'] });
    },
  });
}

export function useLicenseOrderAction(orderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (!orderId && body.action !== 'create_manual_order') {
        throw new Error('orderId requerido');
      }
      const pathId = orderId || '00000000-0000-4000-8000-000000000000';
      const primary = await apiClient.post<{
        ok: boolean;
        status?: string;
        invoiceId?: string;
        invoiceNumber?: string;
        operations?: unknown;
        id?: string;
        publicId?: string;
        power?: {
          status?: string;
          missing?: Array<{ code: string; label: string }>;
        };
      }>(`/ops/license-orders/${pathId}/action`, body);
      if (!primary.error) return primary.data;
      // La function dedicada puede no estar aún en el deploy vivo; mismo
      // fallback que el detalle: license-orders-create.
      if (primary.error.status === 404) {
        const fallback = await apiClient.post<{
          ok: boolean;
          status?: string;
          invoiceId?: string;
          invoiceNumber?: string;
          power?: {
            status?: string;
            missing?: Array<{ code: string; label: string }>;
          };
        }>(`/license-orders?orderId=${encodeURIComponent(pathId)}`, {
          ...body,
          orderId: orderId || undefined,
        });
        if (fallback.error) throw new Error(fallback.error.message);
        return fallback.data;
      }
      throw new Error(primary.error.message);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'license-orders'] });
    },
  });
}

export function useToggleLicenseRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ recipeId, status }: { recipeId: string; status: string }) => {
      const { error } = await dbClient.rpc('admin_license_recipe_toggle', {
        p_recipe_id: recipeId,
        p_status: status,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.recipes });
    },
  });
}

function serializeDurationOptions(durationOptions: LicenseDurationOption[]) {
  return durationOptions.map((duration) => {
    const labelEn =
      typeof duration.labelEn === 'string' ? duration.labelEn.trim().slice(0, 80) : '';
    const row: {
      code: string;
      label: string;
      label_en: string | null;
      official_fee_cents: number;
      exemption?: string;
      duration_map?: string | null;
      fee_variants?: Array<{
        code: string;
        label: string;
        label_en: string | null;
        official_fee_cents: number;
        kind?: string;
        additive?: boolean;
      }>;
    } = {
      code: duration.code,
      label: duration.label,
      label_en: labelEn || null,
      official_fee_cents: duration.officialFeeCents,
    };
    if (duration.exemption) row.exemption = duration.exemption;
    if (duration.durationMap === null) row.duration_map = null;
    else if (duration.durationMap) row.duration_map = duration.durationMap;
    if (duration.feeVariants?.length) {
      row.fee_variants = duration.feeVariants.map((variant) => {
        const variantLabelEn =
          typeof variant.labelEn === 'string' ? variant.labelEn.trim().slice(0, 80) : '';
        return {
          code: variant.code,
          label: variant.label,
          label_en: variantLabelEn || null,
          official_fee_cents: variant.officialFeeCents,
          ...(variant.kind ? { kind: variant.kind } : {}),
          ...(variant.additive ? { additive: true } : {}),
        };
      });
    }
    return row;
  });
}

export function useCreateLicenseRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LicenseRecipeCreateInput) => {
      const { error } = await dbClient.rpc('admin_license_recipe_create', {
        p_ccaa: input.ccaa,
        p_modality: input.modality,
        p_subterritorio: input.subterritorio || '',
        p_tier: input.tier,
        p_status: input.status,
        p_sede_url: input.sedeUrl,
        p_display_name: input.displayName,
        p_duration_options: serializeDurationOptions(input.durationOptions),
        p_checkout_requirements: input.checkoutRequirements || [],
        p_sale_mode: input.saleMode || 'online',
        p_whatsapp_message: input.whatsappMessage || '',
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.recipes });
    },
  });
}

export function useUpdateLicenseRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LicenseRecipeUpdateInput) => {
      const { error } = await dbClient.rpc('admin_license_recipe_update', {
        p_recipe_id: input.recipeId,
        p_tier: input.tier,
        p_sede_url: input.sedeUrl,
        p_display_name: input.displayName,
        p_duration_options: serializeDurationOptions(input.durationOptions),
        p_subterritorio: input.subterritorio ?? '',
        p_checkout_requirements: input.checkoutRequirements || [],
        p_sale_mode: input.saleMode || 'online',
        p_whatsapp_message: input.whatsappMessage || '',
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.recipes });
    },
  });
}

export function useArchiveLicenseRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (recipeId: string) => {
      const { error } = await dbClient.rpc('admin_license_recipe_archive', {
        p_recipe_id: recipeId,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.recipes });
    },
  });
}
